import { ColumnDef } from "@tanstack/react-table";

// Helper functions
const enumToStringConverter = (value: string) => {
	if (!value) return "";
	return value
		.split("_")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(" ");
};

const moneyFormat = (amount: number) => {
	if (!amount && amount !== 0) return "0.00";
	return amount.toLocaleString("en-US", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
};

const formatDateAndTime = (params: any) => {
	if (!params.value) return "";
	const date = new Date(params.value);
	return date.toLocaleString();
};

const getAllowanceActionColor = (status: string) => {
	switch (status) {
		case "ADDITION":
			return "#4CAF50";
		case "REMOVAL":
			return "#F44336";
		case "UPDATE":
			return "#2196F3";
		default:
			return "#BF21F3";
	}
};

// ==================== EMPLOYEE ENGAGEMENT HISTORY COLUMNS ====================
export const employeeEngagementHistoryColumns: ColumnDef<any>[] = [
	{
		accessorKey: "recordedAt",
		header: "Recorded At",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => formatDateAndTime({ value: getValue() }),
	},
	{
		accessorKey: "jobTitle",
		header: "Job Title",
		size: 200,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => {
			const jobTitle = getValue() as any;
			return jobTitle?.name || "--";
		},
	},
	{
		accessorKey: "contractType",
		header: "Contract Type",
		size: 200,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => {
			const contractType = getValue() as any;
			return contractType?.name || "--";
		},
	},
	{
		accessorKey: "basicSalary",
		header: "Basic Salary",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ row }) => {
			const amount = row.original.basicSalary;
			const currency = row.original.currency;
			return amount ? `${currency?.code || ""} ${moneyFormat(amount)}` : "--";
		},
	},
	{
		accessorKey: "actor",
		header: "Actor",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => {
			const actor = getValue() as any;
			return actor?.name || "User";
		},
	},
];

// ==================== EMPLOYEE BASIC SALARY HISTORY COLUMNS ====================
export const employeeBasicSalaryHistoryColumns: ColumnDef<any>[] = [
	{
		accessorKey: "doneAt",
		header: "Recorded At",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => formatDateAndTime({ value: getValue() }),
	},
	{
		accessorKey: "previousValue",
		header: "Previous Value",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ row }) => {
			const amount = row.original.previousValue;
			const currency = row.original.previousCurrency;
			return amount ? `${currency?.code || ""} ${moneyFormat(amount)}` : "--";
		},
	},
	{
		accessorKey: "currentValue",
		header: "Current Value",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ row }) => {
			const amount = row.original.currentValue;
			const currency = row.original.currentCurrency;
			return amount ? `${currency?.code || ""} ${moneyFormat(amount)}` : "--";
		},
	},
	{
		accessorKey: "actor",
		header: "Actor",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => {
			const actor = getValue() as any;
			return actor?.name || "User";
		},
	},
];

// ==================== EMPLOYEE JOB TITLE HISTORY COLUMNS ====================
export const employeeJobTitleHistoryColumns: ColumnDef<any>[] = [
	{
		accessorKey: "doneAt",
		header: "Recorded At",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => formatDateAndTime({ value: getValue() }),
	},
	{
		accessorKey: "previousValue",
		header: "Previous Value",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => {
			const value = getValue() as any;
			return value?.name || "--";
		},
	},
	{
		accessorKey: "currentValue",
		header: "Current Value",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => {
			const value = getValue() as any;
			return value?.name || "--";
		},
	},
	{
		accessorKey: "actor",
		header: "Actor",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => {
			const actor = getValue() as any;
			return actor?.name || "User";
		},
	},
];

// ==================== EMPLOYEE BAND HISTORY COLUMNS ====================
export const employeeBandHistoryColumns: ColumnDef<any>[] = [
	{
		accessorKey: "doneAt",
		header: "Recorded At",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => formatDateAndTime({ value: getValue() }),
	},
	{
		accessorKey: "previousValue",
		header: "Previous Value",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => {
			const value = getValue() as any;
			return value?.name || "--";
		},
	},
	{
		accessorKey: "currentValue",
		header: "Current Value",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => {
			const value = getValue() as any;
			return value?.name || "--";
		},
	},
	{
		accessorKey: "actor",
		header: "Actor",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => {
			const actor = getValue() as any;
			return actor?.name || "User";
		},
	},
];

// ==================== EMPLOYEE NOTCH HISTORY COLUMNS ====================
export const employeeNotchHistoryColumns: ColumnDef<any>[] = [
	{
		accessorKey: "doneAt",
		header: "Recorded At",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => formatDateAndTime({ value: getValue() }),
	},
	{
		accessorKey: "previousValue",
		header: "Previous Value (Employee Band)",
		size: 260,
		minSize: 220,
		maxSize: 400,
		cell: ({ getValue }) => {
			const value = getValue() as any;
			if (!value) return "--";
			return (
				value.code + (value.employeeBand ? ` (${value.employeeBand.name})` : "")
			);
		},
	},
	{
		accessorKey: "currentValue",
		header: "Current Value (Employee Band)",
		size: 260,
		minSize: 220,
		maxSize: 400,
		cell: ({ getValue }) => {
			const value = getValue() as any;
			if (!value) return "--";
			return (
				value.code + (value.employeeBand ? ` (${value.employeeBand.name})` : "")
			);
		},
	},
	{
		accessorKey: "actor",
		header: "Actor",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => {
			const actor = getValue() as any;
			return actor?.name || "User";
		},
	},
];

// ==================== EMPLOYEE ALLOWANCE HISTORY COLUMNS ====================
export const employeeAllowanceHistoryColumns: ColumnDef<any>[] = [
	{
		accessorKey: "doneAt",
		header: "Recorded At",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => formatDateAndTime({ value: getValue() }),
	},
	{
		accessorKey: "previousValue",
		header: "Previous Value",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => {
			const value = getValue() as any;
			return value?.name || "--";
		},
	},
	{
		accessorKey: "currentValue",
		header: "Current Value",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => {
			const value = getValue() as any;
			return value?.name || "--";
		},
	},
	{
		accessorKey: "action",
		header: "Action",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => {
			const action = getValue() as string;
			return (
				<div
					className="rounded-lg px-4 py-2 min-w-[80px] text-center relative font-semibold overflow-hidden"
					style={{ color: getAllowanceActionColor(action) }}
				>
					<div
						className="absolute inset-0"
						style={{
							background: getAllowanceActionColor(action),
							opacity: 0.15,
						}}
					/>
					<p>{enumToStringConverter(action)}</p>
				</div>
			);
		},
	},
	{
		accessorKey: "actor",
		header: "Actor",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => {
			const actor = getValue() as any;
			return actor?.name || "User";
		},
	},
];

// ==================== EMPLOYEE QUALIFICATION HISTORY COLUMNS ====================
export const employeeQualificationHistoryColumns: ColumnDef<any>[] = [
	{
		accessorKey: "doneAt",
		header: "Recorded At",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => formatDateAndTime({ value: getValue() }),
	},
	{
		accessorKey: "previousValue",
		header: "Previous Value",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => {
			const value = getValue() as any;
			return value?.qualification || "--";
		},
	},
	{
		accessorKey: "currentValue",
		header: "Current Value",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => {
			const value = getValue() as any;
			return value?.qualification || "--";
		},
	},
	{
		accessorKey: "actor",
		header: "Actor",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => {
			const actor = getValue() as any;
			return actor?.name || "User";
		},
	},
];

// ==================== EMPLOYEE DISENGAGEMENT HISTORY COLUMNS ====================
export const employeeDisengagementHistoryColumns: ColumnDef<any>[] = [
	{
		accessorKey: "recordedAt",
		header: "Recorded At",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => formatDateAndTime({ value: getValue() }),
	},
	{
		accessorKey: "reason",
		header: "Reason",
		size: 400,
		minSize: 300,
		maxSize: 600,
	},
	{
		accessorKey: "jobTitle",
		header: "Job Title",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => {
			const jobTitle = getValue() as any;
			return jobTitle?.name || "--";
		},
	},
	{
		accessorKey: "contractType",
		header: "Contract Type",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => {
			const contractType = getValue() as any;
			return contractType?.name || "--";
		},
	},
	{
		accessorKey: "actor",
		header: "Actor",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => {
			const actor = getValue() as any;
			return actor?.name || "User";
		},
	},
];
